/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { ActionTree, ActionContext } from 'vuex';

import { Service } from '@/pages/survey_builder/types';

import * as types from './mutationTypes';
import { additionalTypeQuestionHasText, getSurveyData, questionHasConditions } from './helpers';
import { State, Question, QuestionType, QuestionOption } from './state';

type ActionsDef = ReturnType<typeof createActions>;
type PayloadType<T> = T extends (ctx: any, payload: infer P) => any ? P : any;
export type Actions = { [P in keyof ActionsDef]: (payload: PayloadType<ActionsDef[P]>) => ReturnType<ActionsDef[P]> };

const createActions = (service: Service) => {
  const handleError = (err: any, dispatch: any) => {
    let msg = 'Unknown error.';

    if (err && err.response && err.response.data && err.response.data.message) {
      msg = err.response.data.message;
    }

    service.showError(msg, dispatch);
  };

  class ActionsDef {
    init({ dispatch }: ActionContext, surveyId: string) {
      if (surveyId === 'new') {
        return dispatch('empty');
      } else {
        return dispatch('load', parseInt(surveyId, 10));
      }
    }
    load({ dispatch, commit, rootState }: ActionContext, surveyId: number) {
      commit(types.LOADING);

      return service.getSurvey(surveyId).then(
        surveyData => {
          surveyData.addMappings = rootState.featureFlags ? rootState.featureFlags.vendor_risk_phase_1 : false;
          commit(types.LOADED, surveyData);
        },
        err => {
          handleError(err, dispatch);
        }
      );
    }
    reset({ dispatch, state }: ActionContext) {
      const msg = 'Do you really want to reset current changes?';

      return service.confirm(msg, dispatch).then(reset => {
        if (reset) {
          if (state.surveyId == null) {
            return dispatch('empty');
          } else {
            return dispatch('load', state.surveyId);
          }
        }
      });
    }
    empty({ commit }: ActionContext) {
      commit(types.EMPTY);

      return Promise.resolve();
    }
    save({ dispatch, commit, state, rootGetters, rootState }: ActionContext) {
      const surveyData = getSurveyData(state, rootState);

      commit(types.SAVING);

      (state.surveyId == null
        ? service.createSurvey(surveyData, rootGetters.csrfToken)
        : service.updateSurvey(state.surveyId, surveyData, rootGetters.csrfToken)
      ).then(
        newSurveyData => {
          newSurveyData.addMappings = rootState.featureFlags ? rootState.featureFlags.vendor_risk_phase_1 : false;
          commit(types.LOADED, newSurveyData);
          commit(types.SAVED);
        },
        err => {
          handleError(err, dispatch);

          commit(types.SAVING_ERROR);
        }
      );
    }
    saveCopy({ dispatch, commit, state, rootGetters, rootState }: ActionContext) {
      const surveyData = getSurveyData(state, rootState);

      commit(types.SAVING);

      service.createSurvey(surveyData, rootGetters.csrfToken).then(
        newSurveyData => {
          newSurveyData.addMappings = rootState.featureFlags ? rootState.featureFlags.vendor_risk_phase_1 : false;
          commit(types.LOADED, newSurveyData);
          commit(types.SAVED);
        },
        err => {
          handleError(err, dispatch);

          commit(types.SAVING_ERROR);
        }
      );
    }
    showPreview({ commit }: ActionContext) {
      commit(types.SHOW_PREVIEW);
    }
    hidePreview({ commit }: ActionContext) {
      commit(types.HIDE_PREVIEW);
    }

    setTitle({ commit }: ActionContext, title: string) {
      commit(types.SET_TITLE, title);
    }
    setWeighted({ commit }: ActionContext, weighted: number) {
      commit(types.SET_WEIGHTED, weighted);
    }
    setMidRiskThreshold({ commit }: ActionContext, midRiskThreshold: string) {
      commit(types.SET_MID_RISK_THRESHOLD, midRiskThreshold);
    }
    setHighRiskThreshold({ commit }: ActionContext, highRiskThreshold: string) {
      commit(types.SET_HIGH_RISK_THRESHOLD, highRiskThreshold);
    }
    addQuestion({ commit }: ActionContext) {
      commit(types.ADD_QUESTION);
    }
    addAnotherQuestion({ commit }: ActionContext) {
      commit(types.ADD_ANOTHER_QUESTION);
    }
    editQuestion({ commit }: ActionContext, questionId: number) {
      commit(types.EDIT_QUESTION, questionId);
    }
    setQuestionsOrder({ commit }: ActionContext, questions: Question[]) {
      commit(types.SET_QUESTIONS_ORDER, questions);
    }
    setPage({ commit }: ActionContext, page: number) {
      commit(types.SET_CURRENT_PAGE, page);
    }
    goToPreviousPage({ commit, getters }: ActionContext) {
      if (getters.hasPreviousPage) {
        commit(types.SET_CURRENT_PAGE, getters.currentPage - 1);
      }
    }
    goToNextPage({ commit, getters }: ActionContext) {
      if (getters.hasNextPage) {
        commit(types.SET_CURRENT_PAGE, getters.currentPage + 1);
      }
    }
    addPage({ commit }: ActionContext) {
      commit(types.ADD_PAGE);
    }
    removePage({ dispatch, commit, getters }: ActionContext) {
      let removePromise = Promise.resolve(true);

      if (getters.currentQuestions.length) {
        const msg = 'This page is not empty. Do you really want to remove this page?';

        removePromise = service.confirm(msg, dispatch);
      }

      return removePromise.then(remove => {
        if (remove) {
          commit(types.REMOVE_PAGE);
        }
      });
    }

    setQuestionType({ dispatch, commit, getters, state }: ActionContext, type: QuestionType) {
      if (additionalTypeQuestionHasText(getters.editQuestion)) {
        const msg = 'Custom formatting of "Text" field will be lost!';

        service.confirm(msg, dispatch).then(confirmChange => {
          if (confirmChange) {
            commit(types.SET_QUESTION_TYPE, {
              questionId: state.activeQuestionId,
              type,
            });
          }
        });
      } else {
        commit(types.SET_QUESTION_TYPE, {
          questionId: state.activeQuestionId,
          type,
        });
      }
    }
    setQuestionTitle({ commit, state }: ActionContext, title: string) {
      commit(types.SET_QUESTION_TITLE, {
        questionId: state.activeQuestionId,
        title,
      });
    }
    setQuestionDescription({ commit, state }: ActionContext, description: string) {
      commit(types.SET_QUESTION_DESCRIPTION, {
        questionId: state.activeQuestionId,
        description,
      });
    }
    setQuestionRequired({ commit, state }: ActionContext, required: boolean) {
      commit(types.SET_QUESTION_REQUIRED, {
        questionId: state.activeQuestionId,
        required,
      });
    }
    setQuestionWeight({ commit, state }: ActionContext, weight: number) {
      commit(types.SET_QUESTION_WEIGHT, {
        questionId: state.activeQuestionId,
        weight,
      });
    }
    setQuestionPage({ commit, state }: ActionContext, page: number) {
      commit(types.SET_QUESTION_PAGE, {
        questionId: state.activeQuestionId,
        page,
      });
    }
    setQuestionOptionTitle({ commit, state }: ActionContext, { optionId, title }: { optionId: number; title: string }) {
      commit(types.SET_QUESTION_OPTION_TITLE, {
        questionId: state.activeQuestionId,
        optionId,
        title,
      });
    }
    setQuestionOptionMultiplier(
      { commit, state }: ActionContext,
      { optionId, multiplier }: { optionId: number; multiplier: number }
    ) {
      commit(types.SET_QUESTION_OPTION_MULTIPLIER, {
        questionId: state.activeQuestionId,
        optionId,
        multiplier,
      });
    }
    addQuestionOption({ commit, state }: ActionContext) {
      commit(types.ADD_QUESTION_OPTION, state.activeQuestionId);
    }
    removeQuestionOption({ commit, state }: ActionContext, optionId: number) {
      commit(types.REMOVE_QUESTION_OPTION, {
        questionId: state.activeQuestionId,
        optionId,
      });
    }
    addQuestionOptionSubquestion({ commit, state }: ActionContext, optionId: number) {
      commit(types.ADD_QUESTION_OPTION_SUBQUESTION, {
        questionId: state.activeQuestionId,
        optionId,
      });
    }
    setQuestionOptionsOrder({ commit, state }: ActionContext, options: QuestionOption[]) {
      commit(types.SET_QUESTION_OPTIONS_ORDER, {
        questionId: state.activeQuestionId,
        options,
      });
    }
    addQuestionCondition({ commit, state }: ActionContext) {
      commit(types.ADD_QUESTION_CONDITION, state.activeQuestionId);
    }
    removeQuestionCondition({ commit, state }: ActionContext, conditionId: number) {
      commit(types.REMOVE_QUESTION_CONDITION, {
        questionId: state.activeQuestionId,
        conditionId,
      });
    }
    setQuestionConditionQuestion(
      { commit, state }: ActionContext,
      { conditionId, questionId }: { conditionId: number; questionId: number }
    ) {
      commit(types.SET_QUESTION_CONDITION_QUESTION, {
        questionId: state.activeQuestionId,
        conditionId,
        conditionQuestionId: questionId,
      });
    }
    setQuestionConditionOption(
      { commit, state }: ActionContext,
      { conditionId, optionId }: { conditionId: number; optionId: number }
    ) {
      commit(types.SET_QUESTION_CONDITION_OPTION, {
        questionId: state.activeQuestionId,
        conditionId,
        conditionOptionId: optionId,
      });
    }
    setQuestionVisibleAlways({ dispatch, commit, getters, state }: ActionContext) {
      const msg = 'This will reset currently set conditions. Continue?';

      if (questionHasConditions(getters.editQuestion)) {
        return service.confirm(msg, dispatch).then(doSwitch => {
          if (doSwitch) {
            commit(types.SET_QUESTION_VISIBLE_ALWAYS, state.activeQuestionId);
          }
        });
      } else {
        commit(types.SET_QUESTION_VISIBLE_ALWAYS, state.activeQuestionId);
      }
    }
    setQuestionVisibleConditional({ commit, state }: ActionContext) {
      commit(types.SET_QUESTION_VISIBLE_CONDITIONAL, state.activeQuestionId);
    }
    removeQuestion({ dispatch, commit }: ActionContext, questionId: number) {
      const msg = 'Do you really want to remove this question?';

      return service.confirm(msg, dispatch).then(remove => {
        if (remove) {
          commit(types.REMOVE_QUESTION, questionId);
        }
      });
    }
    duplicateQuestion({ commit, state, rootState }: ActionContext) {
      const addMappings = rootState.featureFlags ? rootState.featureFlags.vendor_risk_phase_1 : false;
      commit(types.DUPLICATE_QUESTION, {
        questionId: state.activeQuestionId,
        addMappings,
      });
    }
  }

  return ActionsDef.prototype;
};

export default (createActions as any) as (service: Service) => ActionTree<State, any>;
